import React, { useCallback, useMemo } from 'react';
import {
  View,
  FlatList,
  RefreshControl,
  StatusBar,
  StyleSheet,
  TouchableOpacity,
  Text,
  ActivityIndicator,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useResidentScreen } from '@/hooks/residents/useResidentScreen';
import { useGetMyThreadsQuery } from '@/store/api/corkBoard/corkBoardApi';
import { useUserThreadsList } from '@/hooks/residents/corkBoard/useUserThreadsList';
import { ResidentMobileHeaderWithMenu } from '@/components/residents/mobile/ResidentMobileHeaderWithMenu';
import { MobileScreenLayout } from '@/components/residents/mobile/MobileScreenLayout';
import {
  ThreadCard,
  ThreadCardSkeleton,
  CorkBoardEmptyState,
} from '@/components/residents/corkBoard';
import { SEMANTIC_COLORS } from '@/constants/residents/residentColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import type { CorkBoardThreadSummary } from '@/types/residents/corkBoard';

type MyThreadsNavigationProp = StackNavigationProp<RootStackParamList>;

export const MyThreadsScreen: React.FC = () => {
  const navigation = useNavigation<MyThreadsNavigationProp>();
  const { layoutMode } = useResponsive();

  const isMobile = layoutMode === 'mobile';
  const isTablet = layoutMode === 'tablet';

  const {
    handleNavigate,
    handleLogoPress,
    handleSignOut,
    handleNotificationPress,
  } = useResidentScreen('MyThreads', navigation as any);

  const {
    threads,
    isInitialLoad,
    isRefreshing,
    isLoadingMore,
    isError,
    handleRefresh,
    handleEndReached,
  } = useUserThreadsList({ queryHook: useGetMyThreadsQuery, variant: 'my' });

  const handleThreadPress = useCallback((thread: CorkBoardThreadSummary) => {
    navigation.navigate('ThreadDetail', { threadId: thread.id });
  }, [navigation]);

  const handleCreatePost = useCallback(() => {
    navigation.navigate('CreateThread');
  }, [navigation]);

  const handleBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('CommunityBoard');
    }
  }, [navigation]);

  const renderThread = useCallback(({ item }: { item: CorkBoardThreadSummary }) => (
    <ThreadCard
      thread={item}
      onPress={handleThreadPress}
    />
  ), [handleThreadPress]);

  const keyExtractor = useCallback((item: CorkBoardThreadSummary) => item.id, []);

  const ListEmptyComponent = useMemo(() => {
    if (isInitialLoad) return <ThreadCardSkeleton />;
    if (isError) return <CorkBoardEmptyState variant="error" onRetry={handleRefresh} />;
    return <CorkBoardEmptyState variant="myThreads" onCreatePost={handleCreatePost} />;
  }, [isInitialLoad, isError, handleRefresh, handleCreatePost]);

  const ListFooterComponent = useMemo(() => {
    if (!isLoadingMore) return null;
    return (
      <View style={styles.footerLoader}>
        <ActivityIndicator size="small" color={SEMANTIC_COLORS.interactive.primary} />
      </View>
    );
  }, [isLoadingMore]);

  const ListHeaderComponent = useMemo(() => (
    <View style={styles.headerSection}>
      <Text style={styles.headerSubtitle}>Posts you've created</Text>
    </View>
  ), []);

  const renderList = () => (
    <FlatList
      data={threads}
      renderItem={renderThread}
      keyExtractor={keyExtractor}
      ListHeaderComponent={ListHeaderComponent}
      ListEmptyComponent={ListEmptyComponent}
      ListFooterComponent={ListFooterComponent}
      contentContainerStyle={styles.listContentContainer}
      ItemSeparatorComponent={ItemSeparator}
      onEndReached={handleEndReached}
      onEndReachedThreshold={0.4}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl
          refreshing={isRefreshing}
          onRefresh={handleRefresh}
          tintColor={SEMANTIC_COLORS.text.primary}
        />
      }
    />
  );

  if (isMobile || isTablet) {
    return (
      <View style={styles.screenContainer}>
        <StatusBar barStyle="light-content" />
        <ResidentMobileHeaderWithMenu
          onLogoPress={handleLogoPress}
          onSignOut={handleSignOut}
          onNavigate={handleNavigate}
          onNotificationPress={handleNotificationPress}
          currentPath="/community-board"
          currentTitle="My Posts"
          showNotification
        />
        <MobileScreenLayout scrollable={false} lightStatusBar={false}>
          <View style={styles.listContainer}>
            <TouchableOpacity onPress={handleBack} style={styles.backButton} activeOpacity={0.7}>
              <RESIDENTS_SVG_COMPONENTS.CHEVRON_LEFT width={16} height={16} color={SEMANTIC_COLORS.interactive.primary} />
              <Text style={styles.backText}>Back to Feed</Text>
            </TouchableOpacity>
            {renderList()}
          </View>
        </MobileScreenLayout>
      </View>
    );
  }

  return (
    <View style={styles.screenContainer}>
      <View style={styles.desktopWrapper}>
        <TouchableOpacity onPress={handleBack} style={styles.backButton} activeOpacity={0.7}>
          <RESIDENTS_SVG_COMPONENTS.CHEVRON_LEFT width={16} height={16} color={SEMANTIC_COLORS.interactive.primary} />
          <Text style={styles.backText}>Back to Feed</Text>
        </TouchableOpacity>
        {renderList()}
      </View>
    </View>
  );
};

const ItemSeparator = () => <View style={styles.separator} />;

const styles = StyleSheet.create({
  screenContainer: {
    flex: 1,
    backgroundColor: SEMANTIC_COLORS.background.primary,
  },
  listContainer: {
    flex: 1,
  },
  desktopWrapper: {
    flex: 1,
    maxWidth: 960,
    alignSelf: 'center',
    width: '100%',
    paddingHorizontal: 40,
  },
  listContentContainer: {
    paddingBottom: 40,
  },
  headerSection: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 12,
  },
  headerSubtitle: {
    fontSize: 13,
    color: SEMANTIC_COLORS.text.secondary,
  },
  separator: {
    height: 10,
  },
  footerLoader: {
    paddingVertical: 20,
    alignItems: 'center',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  backText: {
    fontSize: 14,
    color: SEMANTIC_COLORS.interactive.primary,
    fontWeight: '500',
  },
});
